import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import { apiService } from '../utils/api';
import ForgotPasswordModal from './ui/ForgotPasswordModal';
import { resolvePostLoginRoute } from '../utils/postLoginRouting';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const { updateUserInfo } = useSessionPersistence();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  if (!isOpen) return null;

  const navigatePostLogin = async (accountToken: string) => {
    const route = await resolvePostLoginRoute(accountToken);

    if (route.state) {
      navigate(route.path, { state: route.state });
    } else {
      navigate(route.path);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await apiService.login(email.trim(), password);
      
      // Check for account lockout
      if (result.lockedUntil || result.remainingSeconds) {
        const hours = Math.ceil((result.remainingSeconds || 0) / 3600);
        setError(`Account temporarily locked. Try again in ${hours} hour(s).`);
        return;
      }
      
      // Check if MFA is required
      if (result.success && result.requiresMFA) {
        // Store MFA session data for the verification page
        sessionStorage.setItem('mfa_userId', result.userId || '');
        sessionStorage.setItem('mfa_email', result.email || '');
        sessionStorage.setItem('mfa_name', result.name || '');
        
        // Close modal and navigate to MFA verification
        onClose();
        navigate('/mfa-verify');
        return;
      }
      
      if (!result.success) {
        setError(result.message || 'Login failed');
      } else if (result.user && result.token) {
        // Update session info
        updateUserInfo({
          email: result.user.email,
          name: result.user.name,
          accountId: result.user.id,
          accountToken: result.token
        });
        // Close the modal first
        onClose();
        
        // Call the success callback if provided
        if (onLoginSuccess) onLoginSuccess();

        await navigatePostLogin(result.token);
      }
    } catch (e: unknown) {
      setError(e.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowForgotPassword(true);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none md:items-start md:justify-end">
        <div className="bg-white border border-gray-300 rounded-lg shadow-lg w-80 p-6 pointer-events-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Log In</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none"
              aria-label="Close login modal"
            >
              ×
            </button>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-600"
                required
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-600"
                required
              />
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="text-sm text-cyan-600 hover:text-cyan-500"
              >
                Forgot password?
              </button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-cyan-600 text-white rounded-md py-2 text-sm font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
};

export default LoginModal;